// 거래명세서 데이터 소스 (C410).
//
// apiClient는 prefixUrl을 쓰므로 경로 앞에 '/'를 붙이지 않는다.

import { apiClient } from '@/lib/api-client';
import type {
  CancelRequest,
  Car,
  DealerDto,
  GenerateRequest,
  GenerateResult,
  InvoiceDocumentResponse,
  PetrohqApiResponseGenerateResult,
  PetrohqApiResponseInteger,
  PetrohqApiResponseInvoiceDocumentResponse,
  PetrohqApiResponseListCar,
  PetrohqApiResponseListDealerDto,
  PetrohqApiResponseListSalesRow,
  PetrohqApiResponseListStatementSummary,
  PetrohqApiResponseTaxGenerateResult,
  PetrohqApiResponseTaxInvoiceView,
  SalesRow,
  StatementSummary,
  TaxGenerateRequest,
  TaxGenerateResult,
  TaxInvoiceView,
} from '@/lib/api/data-contracts';

export interface TargetDealerParams {
  storeCd: string;
  issueTerm: string;
  /** 발행기간유형 6(수시)이면 필수 */
  dealCode?: string;
}

export interface SalesRowParams extends TargetDealerParams {
  startDate: string;
  endDate: string;
  /** 수시 발행에서만 쓰는 필터 */
  geoEcode?: string;
  oilType?: string;
}

export interface StatementParams {
  storeCd: string;
  issueYm: string;
  dealCodes?: string[];
}

export interface InvoiceDocumentParams {
  storeCd: string;
  startDate: string;
  endDate: string;
  dealCodes?: string[];
  geoEcode?: string;
}

function toSearchParams(params: Record<string, string | string[] | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach(item => search.append(key, item));
    } else {
      search.append(key, value);
    }
  }
  return search;
}

/** 좌측 거래내역. 이미 확정된 자료는 issued=true로 내려와 선택 대상에서 빠진다 */
export async function fetchSalesRows(params: SalesRowParams): Promise<SalesRow[]> {
  const response = await apiClient.get('invoices/sales', {
    searchParams: toSearchParams({ ...params }),
  });
  const data = (await response.json()) as PetrohqApiResponseListSalesRow;
  return data.result ?? [];
}

/** 우측 거래명세서 내역(거래처별 집계) */
export async function fetchStatements(
  params: StatementParams
): Promise<StatementSummary[]> {
  const response = await apiClient.get('invoices', {
    searchParams: toSearchParams({ ...params }),
  });
  const data = (await response.json()) as PetrohqApiResponseListStatementSummary;
  return data.result ?? [];
}

/** 확정 생성. 같은 기간에 확정 자료가 섞여 있으면 서버가 거부한다 */
export async function generateStatements(
  request: GenerateRequest
): Promise<GenerateResult | undefined> {
  const response = await apiClient.post('invoices', { json: request });
  const data = (await response.json()) as PetrohqApiResponseGenerateResult;
  return data.result;
}

/** 생성 취소. 동일 발행년월에 세금계산서가 있으면 전체가 차단된다 */
export async function cancelStatements(request: CancelRequest): Promise<number> {
  const response = await apiClient.delete('invoices', { json: request });
  const data = (await response.json()) as PetrohqApiResponseInteger;
  return data.result ?? 0;
}

/**
 * 세금계산서 생성(거래처기준·확장코드기준 분리, 품목 4건 초과 시 문서 분할).
 *
 * 발행할 수 없는 거래처는 요청 전체를 실패시키지 않고 skipped에 사유와 함께 담겨 온다.
 */
export async function generateTaxInvoices(
  request: TaxGenerateRequest
): Promise<TaxGenerateResult> {
  const response = await apiClient.post('tax-invoices', { json: request });
  const data = (await response.json()) as PetrohqApiResponseTaxGenerateResult;
  return data.result ?? {};
}

/** 영수↔청구 토글. 합계금액이 현금↔외상 칸으로 옮겨간다 */
export async function toggleReceiptPurpose(
  taxNo: number
): Promise<TaxInvoiceView | undefined> {
  const response = await apiClient.patch(`tax-invoices/${taxNo}/receipt-purpose`);
  const data = (await response.json()) as PetrohqApiResponseTaxInvoiceView;
  return data.result;
}

/** 문서 단위 비고 저장(§4.3). 수정세금계산서의 당초 승인번호 등. 빈 값이면 지운다 */
export async function updateTaxInvoiceRemark(taxNo: number, remark: string): Promise<void> {
  await apiClient.patch(`tax-invoices/${taxNo}/remark`, { json: { remark } });
}

/** 인쇄용 확정 데이터. 확장코드 라인은 별도 문서로 분리돼 돌아온다 */
export async function fetchInvoiceDocuments(
  params: InvoiceDocumentParams
): Promise<InvoiceDocumentResponse> {
  const response = await apiClient.get('invoices/document', {
    searchParams: toSearchParams({ ...params }),
  });
  const data =
    (await response.json()) as PetrohqApiResponseInvoiceDocumentResponse;
  return data.result ?? {};
}

/** 주유소의 거래처 마스터 - 검색조건 거래처 선택용(정본 DEALERS) */
export async function fetchDealers(storeCd: string): Promise<DealerDto[]> {
  const response = await apiClient.get('master/cust', { searchParams: { storeCodeList: storeCd } });
  const data = (await response.json()) as PetrohqApiResponseListDealerDto;
  return data.result ?? [];
}

/** 주유소의 차량 마스터 - 확장코드 선택용(정본 CARS). 확장코드는 차량의 속성이다 */
export async function fetchCars(storeCd: string): Promise<Car[]> {
  const response = await apiClient.get('master/car', { searchParams: { storeCodeList: storeCd } });
  const data = (await response.json()) as PetrohqApiResponseListCar;
  return data.result ?? [];
}
